import { environment } from '../../environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiFailureError } from '../helpers/api-error.util';


/**
 * Standard connect-service response envelope. Confirmed against
 * `AliasResource` (connect-service): every `/alias/**` endpoint returns
 * `ApiResponse<T>` — same envelope `AdminService` unwraps for
 * `/internal/admin/**` — so this service unwraps `.data` too.
 */
interface ApiResponse<T> {
  code: number;
  status: string;
  message: string;
  data: T;
  error?: string;
}

/**
 * An active Professional Associate returned by the vendor search (the alias
 * runtime's reuse of the existing active-PA search endpoint), as offered to
 * the picker dialog's result list. Mirrors `AliasResource.AliasVendorSummary`.
 */
export interface AliasVendor {
  id: string;
  name: string;
  email: string;
  org: string;
}

/**
 * Paged envelope returned by `GET /alias/vendors` (`.data`, once unwrapped).
 * Mirrors connect-service's `AliasVendorPage` record — `items` is the current
 * page's slice, `total` is the full filtered count (not just this page's
 * size), and `page`/`size` echo the request.
 */
export interface AliasVendorPage {
  items: AliasVendor[];
  total: number;
  page: number;
  size: number;
}

/**
 * Response from `POST /alias/session` (`.data`, once unwrapped). Mirrors
 * `AliasResource.AliasSessionSummary`. `reload` is always `true` on
 * success — carried on the wire as a signal to the caller, though this
 * service's own caller (the picker dialog) already knows to hard-reload
 * regardless; see the class doc below.
 */
export interface AliasSessionStartResponse {
  vendorId: string;
  vendorOrg: string;
  reload: boolean;
}

/**
 * Thin HTTP client for the staff-driven alias (read-only guidance session)
 * endpoints. Unlike `AliasSessionService` (Task 13 — tracks only whether the
 * CURRENT session is aliased, hydrated from `/user/me`), this service issues
 * the requests that start and end a session.
 *
 * `startSession` deliberately does NOT reload the page itself. Per the alias
 * runtime contract, a successful start requires the CALLER to hard-reload so
 * the app re-bootstraps as the vendor: AuthGuard's `GET /user/me` picks up the
 * alias cookie (which takes precedence over the staff's own, untouched
 * session cookie), `AliasSessionService` hydrates from that response, and the
 * existing role-based routing lands the staffer in the vendor's PA portal.
 * Keeping the reload out of this service keeps it unit-testable with
 * `HttpTestingController` — see `AliasVendorPickerDialogComponent`, which owns
 * the reload.
 */
@Injectable({
  providedIn: 'root',
})
export class AliasService {
  constructor(private http: HttpClient) {}

  /**
   * connect-service returns FAILURE envelopes with HTTP 200 (the intended
   * status lives in the envelope's `code`, not the wire status), so RxJS
   * `error` callbacks never fire on their own. This throws on a
   * FAILURE/4xx+ envelope so consumers' `error` handlers run instead of
   * treating a failure as success. Mirrors `AdminService.throwIfFailure`,
   * including its {@link ApiFailureError} type: that is what marks a message as
   * server-authored and therefore safe to show, so callers can pass the error
   * to `serverErrorMessage` and have a transport-level failure fall back to
   * generic copy instead of leaking Angular's raw `HttpErrorResponse` text.
   */
  private throwIfFailure(r: {
    status?: string;
    code?: number;
    message?: string;
    error?: string;
  }): void {
    if (
      r &&
      (r.status === 'FAILURE' || (typeof r.code === 'number' && r.code >= 400))
    ) {
      throw new ApiFailureError(r.message || r.error || 'Request failed', r.code);
    }
  }

  /** Throws on a FAILURE envelope (see {@link throwIfFailure}), then unwraps `.data`. */
  private unwrap<T>(r: ApiResponse<T>): T {
    this.throwIfFailure(r);
    return r.data;
  }

  /** Active PAs matching `term`, one page at a time, for the picker dialog's search box. */
  searchVendors(
    term: string,
    page: number,
    size: number
  ): Observable<AliasVendorPage> {
    const params = new HttpParams()
      .set('search', term ?? '')
      .set('page', String(page))
      .set('size', String(size));
    return this.http
      .get<ApiResponse<AliasVendorPage>>(
        environment.apiUrl + '/alias/vendors',
        { params }
      )
      .pipe(
        map((r) => {
          const data = this.unwrap(r);
          return {
            items: data?.items ?? [],
            total: data?.total ?? 0,
            page: data?.page ?? page,
            size: data?.size ?? size,
          };
        })
      );
  }

  /**
   * Starts a read-only alias session as `vendorId`. On success, the server
   * has already set the alias cookies — the caller must hard-reload for the
   * new session to take effect (see class doc).
   */
  startSession(vendorId: string): Observable<AliasSessionStartResponse> {
    return this.http
      .post<ApiResponse<AliasSessionStartResponse>>(
        environment.apiUrl + '/alias/session',
        { vendorId }
      )
      .pipe(map((r) => this.unwrap(r)));
  }

  /**
   * Ends the current alias session, if any. On success, the server has
   * already cleared the alias cookies, so the staff's own session resurfaces
   * — no re-minting, no logout. Exposed here for Task 16's banner "End
   * session" action.
   */
  endSession(): Observable<unknown> {
    return this.http
      .post<ApiResponse<unknown>>(environment.apiUrl + '/alias/session/end', {})
      .pipe(map((r) => this.unwrap(r)));
  }
}
